// Combined "add frame" modal — handles both:
//   - Pick from template library
//   - Ask agent to suggest a frame from current cards (LLM call)
//   - Preview agent suggestion, then accept/reject
//
// One modal with internal modes keeps the UX coherent. The user lands on
// the picker, sees both options, and the suggest path turns into a preview
// panel inside the same surface.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api';
import type { FrameDef, FrameNode, PackDef } from '../api';

type SuggestProgress = {
  /** Card count, known from the `started` frame. 0 until then. */
  cardCount: number;
  /** Bytes of tool-call JSON the model has emitted so far. 0 means the
   *  server is just sending heartbeats (provider hasn't streamed deltas yet). */
  bytes: number;
  /** Wall time when the suggesting started (ms). Drives the bar when bytes=0. */
  startedAt: number;
  /** Latest tick received (ms). Updates on each heartbeat / real tick. */
  latestTickAt: number;
};

type Stage =
  | { kind: 'picker' }
  | { kind: 'suggesting'; progress: SuggestProgress }
  | { kind: 'preview'; proposal: FrameDef; cardCount: number }
  | { kind: 'committing' };

export interface AddFrameModalProps {
  projectId: string;
  cardCount: number;
  /** Pack id of the current project (`project.pack`). Drives the
   *  "Recommended by {pack}" badges in the picker. null/'blank' = no
   *  recommendations surfaced. */
  projectPack?: string | null;
  /** Called after a frame is successfully created. Receives new frame_id. */
  onCreated: (frameId: string) => void;
  onClose: () => void;
}

export function AddFrameModal({
  projectId, cardCount, projectPack, onCreated, onClose,
}: AddFrameModalProps) {
  const { t } = useTranslation();
  const [stage, setStage] = useState<Stage>({ kind: 'picker' });
  const [templates, setTemplates] = useState<FrameDef[]>([]);
  const [pack, setPack] = useState<PackDef | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.listFrameTemplates(projectId)
      .then(setTemplates)
      .catch((e) => setError(t('frame.errLoadTemplates', { message: e.message })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Resolve the project's pack so we can highlight its recommended
  // templates. Best-effort: fetch failure leaves `pack=null` and the
  // picker silently falls back to no recommendations.
  useEffect(() => {
    const id = projectPack ?? 'blank';
    if (id === 'blank') { setPack(null); return; }
    api.listPacks()
      .then((packs) => setPack(packs.find((p) => p.id === id) ?? null))
      .catch(() => setPack(null));
  }, [projectPack]);

  const askSuggestion = async () => {
    const startedAt = Date.now();
    setStage({
      kind: 'suggesting',
      progress: { cardCount: 0, bytes: 0, startedAt, latestTickAt: startedAt },
    });
    setError(null);
    try {
      const res = await api.suggestFrame(projectId, 6, (evt) => {
        if (evt.type === 'started') {
          setStage({
            kind: 'suggesting',
            progress: {
              cardCount: evt.card_count,
              bytes: 0,
              startedAt,
              latestTickAt: Date.now(),
            },
          });
        } else if (evt.type === 'tick') {
          setStage((prev) =>
            prev.kind === 'suggesting'
              ? {
                  kind: 'suggesting',
                  progress: {
                    ...prev.progress,
                    // keep the largest bytes seen — heartbeats send 0 but we
                    // don't want to regress real progress.
                    bytes: Math.max(prev.progress.bytes, evt.bytes),
                    latestTickAt: Date.now(),
                  },
                }
              : prev,
          );
        }
      });
      setStage({
        kind: 'preview',
        proposal: res.proposal,
        cardCount: res.card_count,
      });
    } catch (e) {
      setError(t('frame.errSuggestFailed', { message: (e as Error).message }));
      setStage({ kind: 'picker' });
    }
  };

  const cloneTemplate = async (tpl: FrameDef) => {
    setStage({ kind: 'committing' });
    try {
      const created = (await api.cloneFrameTemplate(projectId, tpl.id)) as { frame_id: string };
      onCreated(created.frame_id);
    } catch (e) {
      setError(t('frame.errCloneFailed', { message: (e as Error).message }));
      setStage({ kind: 'picker' });
    }
  };

  const acceptProposal = async (proposal: FrameDef) => {
    setStage({ kind: 'committing' });
    try {
      const created = (await api.createCustomFrame(projectId, {
        name: proposal.name,
        description: proposal.description,
        topology: proposal.topology,
        origin: 'agent',
      })) as { frame_id: string };
      onCreated(created.frame_id);
    } catch (e) {
      setError(t('frame.errSaveFailed', { message: (e as Error).message }));
      setStage({ kind: 'preview', proposal, cardCount });
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(26, 24, 20, 0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1500,
        padding: 24,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          background: 'white',
          borderRadius: 8,
          width: 'min(720px, 92vw)',
          maxHeight: '88vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '14px 22px',
            borderBottom: '1px solid var(--rule)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <span className="serif" style={{ fontSize: 18, fontWeight: 500 }}>
            {stage.kind === 'preview' ? t('frame.headingSuggested') : t('frame.headingAdd')}
          </span>
          <span style={{ flex: 1 }} />
          <button
            onClick={onClose}
            className="mono"
            style={{
              fontSize: 12, color: 'var(--ink-3)',
              padding: '4px 10px', border: '1px solid var(--rule)', borderRadius: 4,
            }}
          >
            ✕
          </button>
        </div>

        {error && (
          <div style={{ padding: '8px 22px', background: 'var(--red-soft)', color: 'var(--red)', fontSize: 12 }}>
            {error}
          </div>
        )}

        {stage.kind === 'picker' && (
          <PickerView
            templates={templates}
            cardCount={cardCount}
            pack={pack}
            onPickTemplate={cloneTemplate}
            onAskSuggestion={askSuggestion}
          />
        )}
        {stage.kind === 'suggesting' && <SuggestingView progress={stage.progress} />}
        {stage.kind === 'preview' && (
          <PreviewView
            proposal={stage.proposal}
            cardCount={stage.cardCount}
            onAccept={() => acceptProposal(stage.proposal)}
            onBack={() => setStage({ kind: 'picker' })}
          />
        )}
        {stage.kind === 'committing' && <CommittingView />}
      </div>
    </div>
  );
}

// ── stages ─────────────────────────────────────────────────────────

function PickerView({
  templates, cardCount, pack, onPickTemplate, onAskSuggestion,
}: {
  templates: FrameDef[];
  cardCount: number;
  pack: PackDef | null;
  onPickTemplate: (t: FrameDef) => void;
  onAskSuggestion: () => void;
}) {
  const { t } = useTranslation();
  const canSuggest = cardCount >= 6;
  const recommendedIds = new Set(pack?.recommended_frame_templates ?? []);
  // Sort recommended templates first (preserving original order within
  // each group). The hardcoded list in `frame_templates.rs` is already
  // priority-ordered, so a stable partition is enough.
  const orderedTemplates = recommendedIds.size === 0
    ? templates
    : [
        ...templates.filter((t) => recommendedIds.has(t.id)),
        ...templates.filter((t) => !recommendedIds.has(t.id)),
      ];
  return (
    <div style={{ flex: 1, padding: '20px 22px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 22 }}>
      {/* Suggest from cards (top, prominent) */}
      <section>
        <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: '.1em', fontWeight: 700, marginBottom: 8 }}>
          {t('frame.designSectionHeading')}
        </div>
        <button
          onClick={onAskSuggestion}
          disabled={!canSuggest}
          style={{
            width: '100%',
            textAlign: 'left',
            padding: '14px 16px',
            background: canSuggest ? 'var(--ink-0)' : 'var(--paper-1)',
            color: canSuggest ? 'white' : 'var(--ink-3)',
            border: '1px solid',
            borderColor: canSuggest ? 'var(--ink-0)' : 'var(--rule)',
            borderRadius: 6,
            cursor: canSuggest ? 'pointer' : 'not-allowed',
          }}
        >
          <div className="serif" style={{ fontSize: 16, fontWeight: 500, marginBottom: 4 }}>
            ✦ {t('frame.haveAgentDesign')}
          </div>
          <div style={{ fontSize: 12, opacity: 0.85, lineHeight: 1.5 }}>
            {canSuggest
              ? t('frame.suggestReady', { count: cardCount })
              : t('frame.suggestNeedMore', { count: cardCount })}
          </div>
        </button>
      </section>

      {/* Templates */}
      <section>
        <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: '.1em', fontWeight: 700, marginBottom: 8 }}>
          {t('frame.orPickTemplate')}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {orderedTemplates.map((tpl) => {
            const recommended = recommendedIds.has(tpl.id);
            return (
              <button
                key={tpl.id}
                onClick={() => onPickTemplate(tpl)}
                style={{
                  textAlign: 'left',
                  padding: '12px 14px',
                  background: 'white',
                  border: '1px solid',
                  borderColor: recommended ? 'var(--ink-1, #5a544a)' : 'var(--rule)',
                  borderRadius: 6,
                  cursor: 'pointer',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
                  <span className="serif" style={{ fontSize: 14.5, fontWeight: 500, color: 'var(--ink-0)' }}>
                    {tpl.name}
                  </span>
                  <span className="mono" style={{ fontSize: 9.5, color: 'var(--ink-3)', letterSpacing: '.04em' }}>
                    {tpl.mode === 'rule' ? '· rule' : '· llm'}
                  </span>
                  <span style={{ flex: 1 }} />
                  <span className="mono" style={{ fontSize: 9.5, color: 'var(--ink-3)' }}>
                    {t('frame.nodeCount', { count: tpl.topology.length })}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--ink-2)', lineHeight: 1.5 }}>
                  {tpl.description}
                </div>
                {recommended && pack && (
                  <div
                    className="mono"
                    style={{
                      marginTop: 6,
                      fontSize: 9.5,
                      color: 'var(--ink-1, #5a544a)',
                      letterSpacing: '.04em',
                    }}
                  >
                    {t('frame.recommendedByPack', { pack: pack.name.toLowerCase() })}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function SuggestingView({ progress }: { progress: SuggestProgress }) {
  const { t } = useTranslation();
  // Re-render every 200ms so the time-driven progress bar advances smoothly
  // even when no events are arriving.
  const [, force] = useState(0);
  useEffect(() => {
    const id = setInterval(() => force((n) => n + 1), 200);
    return () => clearInterval(id);
  }, []);

  const elapsedMs = Date.now() - progress.startedAt;
  // GLM batches tool-call JSON, so `bytes` usually stays 0 until the very end.
  // Drive the bar from elapsed time using an asymptote curve that aims for
  // ~95% at the typical 15s completion mark, never quite reaching 100%.
  // Real byte progress (when the provider does stream) overrides time.
  const timePct = 100 * (1 - Math.exp(-elapsedMs / 6000));
  const bytesPct = progress.bytes > 0 ? (progress.bytes / 900) * 100 : 0;
  const pct = Math.min(96, Math.max(timePct, bytesPct));

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 240,
        padding: 22,
        gap: 14,
      }}
    >
      <div style={{ textAlign: 'center', maxWidth: 360 }}>
        <div
          className="mono"
          style={{
            fontSize: 11,
            color: 'var(--blue)',
            letterSpacing: '.1em',
            fontWeight: 700,
            marginBottom: 8,
          }}
        >
          ✦ {t('frame.designingTopology')}
          {progress.cardCount > 0 ? ` · ${t('frame.designingCardCount', { count: progress.cardCount })}` : ''}
          <span className="caret" />
        </div>
        <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.6 }}>
          {t('frame.designingBody', { seconds: Math.round(elapsedMs / 1000) })}
        </div>
      </div>
      <div
        style={{
          width: 280,
          height: 4,
          background: 'var(--paper-1)',
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid var(--rule)',
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: '100%',
            background: 'var(--blue)',
            transition: 'width 220ms ease-out',
          }}
        />
      </div>
    </div>
  );
}

function CommittingView() {
  const { t } = useTranslation();
  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
      <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', letterSpacing: '.1em' }}>
        {t('frame.saving')}<span className="caret" />
      </div>
    </div>
  );
}

function PreviewView({
  proposal, cardCount, onAccept, onBack,
}: {
  proposal: FrameDef;
  cardCount: number;
  onAccept: () => void;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  // Build a visual outline of the proposed topology.
  return (
    <>
      <div style={{ flex: 1, padding: '20px 24px', overflowY: 'auto' }}>
        <div className="serif" style={{ fontSize: 22, fontWeight: 500, color: 'var(--ink-0)', marginBottom: 4 }}>
          {proposal.name}
        </div>
        <div style={{ fontSize: 13, color: 'var(--ink-2)', fontStyle: 'italic', marginBottom: 18, lineHeight: 1.55 }}>
          {proposal.description}
        </div>
        <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: '.1em', fontWeight: 700, marginBottom: 8 }}>
          {t('frame.proposedTopology', { nodes: proposal.topology.length, cards: cardCount })}
        </div>
        <Topology nodes={proposal.topology} />
      </div>
      <div
        style={{
          padding: '12px 22px',
          borderTop: '1px solid var(--rule)',
          background: 'var(--paper-1)',
          display: 'flex',
          gap: 10,
        }}
      >
        <button
          onClick={onBack}
          className="mono"
          style={{
            fontSize: 11.5, padding: '7px 14px',
            background: 'white', color: 'var(--ink-1)',
            border: '1px solid var(--rule)', borderRadius: 4, fontWeight: 600,
          }}
        >
          {t('common.back')}
        </button>
        <span style={{ flex: 1 }} />
        <button
          onClick={onAccept}
          className="mono"
          style={{
            fontSize: 11.5, padding: '7px 14px',
            background: 'var(--ink-0)', color: 'white',
            border: '1px solid var(--ink-0)', borderRadius: 4, fontWeight: 600,
            letterSpacing: '.04em',
          }}
        >
          {t('frame.acceptAndUse')}
        </button>
      </div>
    </>
  );
}

function Topology({ nodes }: { nodes: FrameNode[] }) {
  const { t } = useTranslation();
  // Render top-levels with their direct children indented.
  const tops = nodes.filter((n) => !n.path.includes('/'));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {tops.map((top) => {
        const children = nodes.filter((n) => n.path.startsWith(top.path + '/'));
        return (
          <div key={top.path} style={{ paddingLeft: 0 }}>
            <div
              style={{
                padding: '8px 10px',
                background: top.mece_required ? 'var(--paper-1)' : 'white',
                border: '1px solid',
                borderColor: top.mece_required ? 'var(--ink-0)' : 'var(--rule)',
                borderRadius: 4,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink-0)' }}>{top.label}</span>
                {top.mece_required && (
                  <span className="mono" style={{ fontSize: 9, color: 'var(--ink-0)', letterSpacing: '.05em', fontWeight: 700 }}>
                    {t('frame.meceTag')}
                  </span>
                )}
                <span className="mono" style={{ fontSize: 9.5, color: 'var(--ink-3)', marginLeft: 4 }}>
                  {top.path}
                </span>
              </div>
              {top.description && (
                <div style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 3, lineHeight: 1.5 }}>
                  {top.description}
                </div>
              )}
            </div>
            {children.length > 0 && (
              <div style={{ paddingLeft: 18, marginTop: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {children.map((c) => (
                  <div key={c.path} style={{ padding: '5px 10px', borderLeft: '2px solid var(--rule)', fontSize: 12.5 }}>
                    <span style={{ fontWeight: 500, color: 'var(--ink-1)' }}>{c.label}</span>
                    {c.description && (
                      <span style={{ marginLeft: 6, color: 'var(--ink-3)' }}>— {c.description}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
